import React, { useRef, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const LABELS = {
  disabled: 'Delegated connection is disabled for this environment',
  awaiting_authorization: 'Not connected — authorization required',
  configured_awaiting_verification: 'Authorized — awaiting credential verification',
  verified: 'Connected & verified',
  expired: 'Authorization expired — reconnect required',
};

const errorText = (e) =>
  typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Connection request failed. Please try again.';

export default function NinjaWorkspaceConnect({ assetId, integrationId, connection, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const current = useRef(assetId);
  current.current = assetId;

  const state = connection?.state || 'disabled';
  const showConnect = state === 'awaiting_authorization' || state === 'expired';
  const showVerify = state === 'configured_awaiting_verification';

  const connect = async () => {
    const started = assetId;
    setBusy(true); setError('');
    try {
      const res = await api.post('/ninja/ws/oauth/connect', { asset_id: assetId });
      if (current.current !== started) return;
      if (res.data?.authorization_url) window.location.href = res.data.authorization_url;
    } catch (e) {
      if (current.current === started) { setError(errorText(e)); setBusy(false); }
    }
  };

  const verify = async () => {
    const started = assetId;
    setBusy(true); setError('');
    try {
      await api.post('/ninja/ws/oauth/verify', { asset_id: assetId });
      if (current.current !== started) return;
      if (onRefresh) await onRefresh();
    } catch (e) {
      if (current.current === started) setError(errorText(e));
    } finally {
      if (current.current === started) setBusy(false);
    }
  };

  return (
    <Card data-testid="ninja-connect-panel">
      <CardHeader><CardTitle>NinjaOne delegated connection</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p data-testid="ninja-connect-status">
          {LABELS[state] || LABELS.disabled}
          {state === 'verified' && connection?.expires_at
            ? ` · expires ${new Date(connection.expires_at).toLocaleString()}`
            : ''}
        </p>
        <p className="text-xs text-slate-400">
          Source integration: {integrationId || 'unknown'} · credentials are never shown here.
        </p>
        {error && <p role="alert" data-testid="ninja-connect-error">{error}</p>}
        {showConnect && (
          <Button data-testid="ninja-connect-authorize-btn" disabled={busy || !assetId} onClick={connect}>
            {busy ? 'Starting…' : 'Connect NinjaOne'}
          </Button>
        )}
        {showVerify && (
          <Button data-testid="ninja-connect-verify-btn" disabled={busy} onClick={verify}>
            {busy ? 'Verifying…' : 'Verify credential'}
          </Button>
        )}
        {state === 'disabled' && (
          <p data-testid="ninja-connect-disabled" className="text-sm text-slate-400">No action available.</p>
        )}
      </CardContent>
    </Card>
  );
}
